// Imports
import ___CSS_LOADER_API_SOURCEMAP_IMPORT___ from "../../node_modules/css-loader/dist/runtime/sourceMaps.js";
import ___CSS_LOADER_API_IMPORT___ from "../../node_modules/css-loader/dist/runtime/api.js";
var ___CSS_LOADER_EXPORT___ = ___CSS_LOADER_API_IMPORT___(___CSS_LOADER_API_SOURCEMAP_IMPORT___);
// Module
___CSS_LOADER_EXPORT___.push([module.id, `/*
 * Aether Fleet Ops.
 *
 * Every colour pair here has been measured against WCAG 2.1 AA in both
 * editions: 4.5:1 for text, 3:1 for control borders and the focus ring. The
 * dark edition is the default and the light one is served to anyone whose
 * system asks for it.
 */

:root {
  color-scheme: dark;

  /* Neutral greys, so the two hues below are the only colour on the page. */
  --bg: #1c1d21;
  --surface: #25262c;
  --surface-2: #2e3037;

  /* Blue is for reading: every piece of text that is not one of the things
     below. Orange is for structure and action: headings, ship names and button
     labels, plus the focus ring. Red is kept for errors and destructive
     buttons, so that a failure never reads as ordinary text. */
  --text: #4fb4d8;
  --muted: #6fa0b4;
  --accent: #ef7c2a;
  --danger: #f26d7f;
  /* Set apart from the blue around it without being an action: a ship's hull,
     and the verb that leads a step of a fit comparison. */
  --neutral: #cbcdd2;

  --on-accent: #1c1d21;
  --on-danger: #1c1d21;

  --badge-bg: #1f3743;
  --badge-text: #4fb4d8;

  /* Meets 1.4.11 against --bg, --surface and --surface-2. */
  --border: #848794;
  /* Purely decorative separators, which 1.4.11 does not govern. */
  --rule: #45474f;

  --radius: 8px;
  --gap: 1rem;
  /* The height of a button, shared with rows that must line up with one. */
  --control: 2.5rem;
  --measure: 68rem;
}

@media (prefers-color-scheme: light) {
  :root {
    color-scheme: light;

    --bg: #f1f2f4;
    --surface: #ffffff;
    --surface-2: #f6f7f9;

    /* The same blue, orange and red, darkened until they carry text on a light
       ground. */
    --text: #1a6e8e;
    --muted: #34728c;
    --accent: #a84d0b;
    --danger: #c2203a;
    --neutral: #1c1d21;

    --on-accent: #ffffff;
    --on-danger: #ffffff;

    --badge-bg: #d9ecf3;
    --badge-text: #1a6380;

    --border: #848794;
    --rule: #d5d7dc;
  }
}

*,
*::before,
*::after {
  box-sizing: border-box;
}

body {
  margin: 0;
  background: var(--bg);
  color: var(--text);
  font:
    16px/1.5 'Segoe UI',
    system-ui,
    -apple-system,
    sans-serif;
  /* 1.4.10: the page must reflow, never scroll sideways. */
  overflow-x: hidden;
}

/* A drifting field of stars, decorative only. */
.starfield {
  position: fixed;
  inset: 0;
  z-index: -1;
  pointer-events: none;
  background:
    radial-gradient(1px 1px at 20% 30%, var(--muted), transparent),
    radial-gradient(1px 1px at 70% 60%, var(--muted), transparent),
    radial-gradient(1px 1px at 40% 80%, var(--muted), transparent),
    radial-gradient(1px 1px at 85% 15%, var(--muted), transparent);
  opacity: 0.35;
  animation: drift 90s linear infinite;
}

@keyframes drift {
  to {
    transform: translateY(-40px);
  }
}

.app {
  max-width: var(--measure);
  margin: 0 auto;
  /* Vertical only, so the side gutter below is never overridden. */
  padding-block: 1.5rem;
  padding-inline: 1rem;
}

/* ---------- banner ---------- */

.banner {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: var(--gap);
  padding-bottom: 1rem;
  border-bottom: 1px solid var(--rule);
}

.banner__logo {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin: 0;
  font-size: 1.35rem;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--accent);
}

.banner__glyph {
  color: var(--text);
}

.stats {
  display: flex;
  gap: 1.5rem;
  margin: 0;
}

.stat__label {
  font-size: 0.7rem;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: var(--muted);
}

.stat__value {
  margin: 0;
  font-size: 1.5rem;
  font-variant-numeric: tabular-nums;
  color: var(--text);
}

/* ---------- toolbar ---------- */

/* No padding of its own: the space around the toolbar belongs to the grid it
   sits in, so the fleet heading beside it starts at exactly the same height. */
.toolbar {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

/* The buttons share out the row between them, so the toolbar's edges line up
   with the panel beneath it. The basis stays \`auto\` so that a row too narrow
   for all three still wraps, and each wrapped row is filled in turn. */
.toolbar > .btn {
  flex: 1 1 auto;
}

/* ---------- layout ---------- */

/*
 * The page is one grid of equal columns, and every ship is one cell of it.
 *
 *   row 1      toolbar (2 columns)   | fleet heading (the rest)
 *   rows 2-5   intake  (2 columns)   | ships
 *   rows 6+    ships, wrapping round beneath the intake
 *
 * The intake is two columns wide and four rows tall. Every row after the first
 * is \`1fr\`, and with no height set on the grid that makes them all as tall as
 * the most demanding one. The intake, shared across its four rows, is the most
 * demanding, so four ships and the three gaps between them are exactly as tall
 * as the intake, however tall its contents make it. Nothing here is a measured
 * number that could drift.
 */
.main {
  display: grid;
  gap: var(--gap);
  /* Two columns and a gap hold the three toolbar buttons on one row. */
  grid-template-columns: repeat(auto-fill, minmax(min(100%, 12rem), 1fr));
  grid-template-rows: auto;
  grid-auto-rows: 1fr;
  padding-top: 1rem;
}

.toolbar {
  grid-column: 1 / span 2;
  grid-row: 1;
}

.intake {
  grid-column: 1 / span 2;
  grid-row: 2 / span 4;
}

/* The section stays in the document as the fleet's landmark but makes no box,
   which is what lets its heading and its ships be cells of the grid above. */
.fleet {
  display: contents;
}

.fleet__head {
  grid-column: 3 / -1;
  grid-row: 1;
}

.fleet__empty {
  grid-column: 3 / -1;
}

/* Too narrow to be sure of a third column, so nothing sits beside the intake.
   The toolbar, the intake and the fleet heading each take the full width, and
   the ships follow in as many columns as fit. The intake still spans four
   rows, so a ship is the same height here as in the wider layout. The
   breakpoint is a little above the width where a third column first fits,
   because a scrollbar takes its width out of the grid but not out of the
   media query. */
@media (max-width: 44rem) {
  .main {
    grid-template-rows: auto repeat(4, 1fr) auto;
  }

  .toolbar,
  .intake,
  .fleet__head,
  .fleet__empty {
    grid-column: 1 / -1;
  }

  .fleet__head {
    grid-row: 6;
  }
}

/* ---------- intake ---------- */

/* A column, so that when the grid makes the panel taller than its contents the
   fitting field takes up the difference and the panel is never left with dead
   space at the bottom. That happens when a ship's card needs more height than a
   quarter of the panel, which raises every row. */
.intake {
  display: flex;
  flex-direction: column;
  padding: 1.25rem;
  border: 1px solid var(--rule);
  border-radius: var(--radius);
  background: var(--surface);
}

.intake h2,
.fleet__head h2 {
  /* No bottom margin: the spacing below each heading belongs to its container,
     which is the only thing that knows how tall the row actually is. */
  margin: 0;
  font-size: 1rem;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--accent);
}

.intake h2 {
  margin-bottom: 0.75rem;
}

/* ---------- fields ---------- */

.field {
  margin-bottom: 1rem;
}

/* Takes any spare height in a column container and hands it to its input. */
.field--grow {
  display: flex;
  flex: 1 0 auto;
  flex-direction: column;
}

.field--grow > .field__input {
  flex: 1 0 auto;
}

.field__label {
  display: block;
  margin-bottom: 0.25rem;
  font-size: 0.8rem;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--muted);
}

.field__hint {
  margin: 0 0 0.5rem;
  font-size: 0.8rem;
  color: var(--muted);
}

.field__row {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.field__input {
  flex: 1 1 10rem;
  min-width: 0;
  padding: 0.55rem 0.7rem;
  border: 1px solid var(--border);
  border-radius: var(--radius);
  background: var(--surface-2);
  color: var(--text);
  font: inherit;
}

.field__input--fit {
  width: 100%;
  font-family: ui-monospace, 'Cascadia Mono', 'Consolas', monospace;
  font-size: 0.85rem;
  resize: vertical;
  white-space: pre;
}

.field__input::placeholder {
  color: var(--muted);
}

.field__input[aria-invalid='true'] {
  border-color: var(--danger);
  border-width: 2px;
}

/* ---------- notices ---------- */

.notice {
  display: block;
  margin: 0.4rem 0 0;
  font-size: 0.85rem;
}

/* The parse result stands on its own between two fields and needs the
   breathing room a field's own margin would otherwise give it. */
output.notice {
  margin-bottom: 1rem;
}

.notice--good {
  color: var(--text);
}

/* Colour is never the only carrier: a failure also states its reason in
   words, and the field it refers to is marked aria-invalid. */
.notice--bad {
  color: var(--danger);
}

.notice--muted {
  color: var(--muted);
}

/* ---------- buttons ---------- */

.btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.4rem;
  min-height: var(--control);
  padding: 0.55rem 0.9rem;
  border: 1px solid var(--border);
  border-radius: var(--radius);
  background: var(--surface-2);
  color: var(--accent);
  font: inherit;
  font-size: 0.85rem;
  letter-spacing: 0.04em;
  cursor: pointer;
}

.btn:hover:not(:disabled) {
  border-color: var(--accent);
}

.btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn--primary {
  border-color: var(--accent);
  background: var(--accent);
  color: var(--on-accent);
  font-weight: 600;
}

.btn--danger {
  border-color: var(--danger);
  color: var(--danger);
}

.btn--danger:hover:not(:disabled) {
  background: var(--danger);
  color: var(--on-danger);
}

.btn--wide {
  width: 100%;
}

.icon-btn {
  padding: 0.15rem 0.45rem;
  border: 1px solid transparent;
  border-radius: var(--radius);
  background: none;
  color: var(--accent);
  font-size: 1.25rem;
  line-height: 1;
  cursor: pointer;
}

.icon-btn:hover {
  color: var(--danger);
  border-color: var(--danger);
}

/* A single visible focus indicator for every control, meeting 1.4.11
   against both the page and panel backgrounds. */
:where(a, button, input, textarea, [tabindex]):focus-visible,
label.btn:has(input:focus-visible) {
  outline: 3px solid var(--accent);
  outline-offset: 2px;
}

/* ---------- fleet ---------- */

.fleet__head {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: var(--gap);
  /* As tall as the toolbar beside it whether or not "Clear All" is showing, so
     the heading sits on the same line as the toolbar's labels. */
  min-height: var(--control);
}

@media (max-width: 44rem) {
  /* Stacked, nothing sits beside the heading, so it need not match a button. */
  .fleet__head {
    min-height: 0;
  }
}

/* Fills a ship's cell, with the message centred in it. */
.fleet__empty {
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0;
  padding: 1rem;
  border: 1px dashed var(--rule);
  border-radius: var(--radius);
  color: var(--muted);
  text-align: center;
}

.card {
  position: relative;
  padding: 1rem;
  border: 1px solid var(--rule);
  border-radius: var(--radius);
  background: var(--surface);
}

.card:hover {
  border-color: var(--border);
}

.card__name {
  margin: 0 1.5rem 0.25rem 0;
  font-size: 1.05rem;
}

/* The name is the card's only tab stop; stretching it over the card gives a
   pointer the whole surface without adding a second stop. */
.card__open {
  padding: 0;
  border: 0;
  background: none;
  color: var(--accent);
  font: inherit;
  text-align: left;
  cursor: pointer;
}

.card__open::after {
  content: '';
  position: absolute;
  inset: 0;
  border-radius: var(--radius);
}

.card__hull {
  margin: 0;
  color: var(--neutral);
  font-size: 0.9rem;
}

.card__title {
  margin: 0.15rem 0 0;
  color: var(--muted);
  font-size: 0.85rem;
}

.card__meta {
  margin: 0.5rem 0 0;
  color: var(--muted);
  font-size: 0.8rem;
}

.card__scuttle {
  position: absolute;
  top: 0.5rem;
  right: 0.5rem;
  /* Above the stretched name so it stays clickable. */
  z-index: 1;
  padding: 0.1rem 0.4rem;
  border: 1px solid transparent;
  border-radius: var(--radius);
  background: none;
  color: var(--accent);
  font-size: 1.1rem;
  line-height: 1;
  cursor: pointer;
}

.card__scuttle:hover {
  color: var(--danger);
  border-color: var(--danger);
}

/* ---------- badges ---------- */

.badge {
  padding: 0.1rem 0.45rem;
  border-radius: 999px;
  background: var(--badge-bg);
  color: var(--badge-text);
  font-size: 0.7rem;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  white-space: nowrap;
}

/* ---------- dialogs ---------- */

/* Heights are in \`dvh\`, the viewport as it is right now. On a phone \`vh\` is the
   viewport with the browser's bars hidden, so a dialog sized to it runs off the
   top and bottom of the screen whenever the bars are showing. The \`vh\` line
   before each one is for browsers without \`dvh\`. */
.modal {
  width: min(46rem, calc(100vw - 2rem));
  max-height: calc(100vh - 2rem);
  max-height: calc(100dvh - 2rem);
  padding: 0;
  border: 1px solid var(--border);
  border-radius: var(--radius);
  background: var(--surface);
  color: var(--text);
  overflow: hidden;
}

.modal::backdrop {
  background: rgb(0 0 0 / 65%);
}

.modal__panel {
  display: flex;
  flex-direction: column;
  max-height: calc(100vh - 2rem);
  max-height: calc(100dvh - 2rem);
}

.modal__head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: var(--gap);
  padding: 1rem;
  border-bottom: 1px solid var(--rule);
}

.modal__title {
  margin: 0;
  font-size: 1.1rem;
  color: var(--accent);
}

.modal__subtitle {
  margin: 0.2rem 0 0;
  color: var(--muted);
  font-size: 0.85rem;
}

.modal__body {
  padding: 1rem;
  overflow-y: auto;
}

.modal__foot {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 0.5rem;
  padding: 1rem;
  border-top: 1px solid var(--rule);
}

/* ---------- choices ---------- */

/* A question with named answers: each answer's name, then what it will do. */
.choice__lead {
  margin: 0 0 1rem;
}

.choice {
  margin: 0;
}

.choice__name {
  font-size: 0.8rem;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--accent);
}

.choice__outcome {
  margin: 0.15rem 0 1rem;
}

.choice__outcome:last-child {
  margin-bottom: 0;
}

/* ---------- fit sheet ---------- */

/* Multi-column rather than grid: a fit's sections vary wildly in length — one
   low slot beside eight high slots is normal — and grid rows are all as tall as
   their tallest cell, which strands a short section above a large gap. Columns
   let each section take only the height it needs and flow on. */
.sheet {
  columns: 16rem;
  column-gap: 1rem;
}

.sheet__section {
  /* Keep a section's heading with its list. */
  break-inside: avoid;
  margin-bottom: 1rem;
}

.sheet__section:last-child {
  margin-bottom: 0;
}

.sheet__heading {
  margin: 0 0 0.35rem;
  font-size: 0.75rem;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--accent);
}

.sheet__list {
  margin: 0;
  padding: 0;
  list-style: none;
}

.sheet__item {
  display: flex;
  align-items: baseline;
  gap: 0.4rem;
  padding: 0.25rem 0;
  border-bottom: 1px solid var(--rule);
  font-size: 0.85rem;
}

.sheet__name {
  flex: 1 1 auto;
  min-width: 0;
  overflow-wrap: anywhere;
}

.sheet__qty {
  color: var(--text);
  font-variant-numeric: tabular-nums;
}

.sheet__item--empty {
  color: var(--muted);
  font-style: italic;
}

/* ---------- fit comparison ---------- */

/* What to do, ahead of what to do it to. The word carries the meaning, so
   nothing here depends on colour. A fixed width lines the items up beneath one
   another whichever verb leads the row. */
.diff__verb {
  flex: 0 0 4.25rem;
  color: var(--neutral);
  font-size: 0.75rem;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

/* Where a charge goes, on its own line under the charge. */
.diff__detail {
  display: block;
  color: var(--muted);
  font-size: 0.8rem;
}

/* ---------- registry ---------- */

.registry {
  margin: 0;
  padding: 0;
  list-style: none;
}

.registry__row {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.35rem 0;
  border-bottom: 1px solid var(--rule);
}

/* A ship name, like the ones on the cards. */
.registry__name {
  flex: 1 1 auto;
  min-width: 0;
  overflow-wrap: anywhere;
  color: var(--accent);
}

/* ---------- utilities ---------- */

.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip-path: inset(50%);
  white-space: nowrap;
  border: 0;
}

/* ---------- user preferences ---------- */

@media (prefers-reduced-motion: reduce) {
  *,
  *::before,
  *::after {
    animation-duration: 0.01ms !important;
    animation-iteration-count: 1 !important;
    transition-duration: 0.01ms !important;
  }
}

@media (prefers-contrast: more) {
  :root {
    --rule: var(--border);
    --muted: var(--text);
  }
}

/* In forced-colours mode the system palette wins. Keep the structure that
   carries meaning and drop the decoration that would fight it. */
@media (forced-colors: active) {
  .starfield {
    display: none;
  }

  .btn,
  .card,
  .intake,
  .modal,
  .field__input {
    border: 1px solid CanvasText;
  }

  /* The system already paints a disabled control in its own GrayText. Fading
     it as well takes a colour the user chose and makes it fainter. */
  .btn:disabled {
    opacity: 1;
  }

  :where(a, button, input, textarea):focus-visible,
  label.btn:has(input:focus-visible) {
    outline: 3px solid Highlight;
  }
}
`, "",{"version":3,"sources":["webpack://./src/styles/app.css"],"names":[],"mappings":"AAAA;;;;;;;EAOE;;AAEF;EACE,kBAAkB;;EAElB,0EAA0E;EAC1E,aAAa;EACb,kBAAkB;EAClB,oBAAoB;;EAEpB;;;+DAG6D;EAC7D,eAAe;EACf,gBAAgB;EAChB,iBAAiB;EACjB,iBAAiB;EACjB;0DACwD;EACxD,kBAAkB;;EAElB,oBAAoB;EACpB,oBAAoB;;EAEpB,mBAAmB;EACnB,qBAAqB;;EAErB,0DAA0D;EAC1D,iBAAiB;EACjB,gEAAgE;EAChE,eAAe;;EAEf,aAAa;EACb,WAAW;EACX,yEAAyE;EACzE,iBAAiB;EACjB,gBAAgB;AAClB;;AAEA;EACE;IACE,mBAAmB;;IAEnB,aAAa;IACb,kBAAkB;IAClB,oBAAoB;;IAEpB;gBACY;IACZ,eAAe;IACf,gBAAgB;IAChB,iBAAiB;IACjB,iBAAiB;IACjB,kBAAkB;;IAElB,oBAAoB;IACpB,oBAAoB;;IAEpB,mBAAmB;IACnB,qBAAqB;;IAErB,iBAAiB;IACjB,eAAe;EACjB;AACF;;AAEA;;;EAGE,sBAAsB;AACxB;;AAEA;EACE,SAAS;EACT,qBAAqB;EACrB,kBAAkB;EAClB;;;;cAIY;EACZ,yDAAyD;EACzD,kBAAkB;AACpB;;AAEA,gDAAgD;AAChD;EACE,eAAe;EACf,QAAQ;EACR,WAAW;EACX,oBAAoB;EACpB;;;;kEAIgE;EAChE,aAAa;EACb,oCAAoC;AACtC;;AAEA;EACE;IACE,4BAA4B;EAC9B;AACF;;AAEA;EACE,yBAAyB;EACzB,cAAc;EACd,iEAAiE;EACjE,qBAAqB;EACrB,oBAAoB;AACtB;;AAEA,iCAAiC;;AAEjC;EACE,aAAa;EACb,eAAe;EACf,mBAAmB;EACnB,8BAA8B;EAC9B,eAAe;EACf,oBAAoB;EACpB,oCAAoC;AACtC;;AAEA;EACE,aAAa;EACb,mBAAmB;EACnB,WAAW;EACX,SAAS;EACT,kBAAkB;EAClB,sBAAsB;EACtB,yBAAyB;EACzB,oBAAoB;AACtB;;AAEA;EACE,kBAAkB;AACpB;;AAEA;EACE,aAAa;EACb,WAAW;EACX,SAAS;AACX;;AAEA;EACE,iBAAiB;EACjB,sBAAsB;EACtB,yBAAyB;EACzB,mBAAmB;AACrB;;AAEA;EACE,SAAS;EACT,iBAAiB;EACjB,kCAAkC;EAClC,kBAAkB;AACpB;;AAEA,kCAAkC;;AAElC;+EAC+E;AAC/E;EACE,aAAa;EACb,eAAe;EACf,WAAW;AACb;;AAEA;;uEAEuE;AACvE;EACE,cAAc;AAChB;;AAEA,iCAAiC;;AAEjC;;;;;;;;;;;;;EAaE;AACF;EACE,aAAa;EACb,eAAe;EACf,qEAAqE;EACrE,uEAAuE;EACvE,wBAAwB;EACxB,mBAAmB;EACnB,iBAAiB;AACnB;;AAEA;EACE,uBAAuB;EACvB,WAAW;AACb;;AAEA;EACE,uBAAuB;EACvB,oBAAoB;AACtB;;AAEA;6EAC6E;AAC7E;EACE,iBAAiB;AACnB;;AAEA;EACE,mBAAmB;EACnB,WAAW;AACb;;AAEA;EACE,mBAAmB;AACrB;;AAEA;;;;;;iBAMiB;AACjB;EACE;IACE,4CAA4C;EAC9C;;EAEA;;;;IAIE,mBAAmB;EACrB;;EAEA;IACE,WAAW;EACb;AACF;;AAEA,iCAAiC;;AAEjC;;;kDAGkD;AAClD;EACE,aAAa;EACb,sBAAsB;EACtB,gBAAgB;EAChB,6BAA6B;EAC7B,4BAA4B;EAC5B,0BAA0B;AAC5B;;AAEA;;EAEE;uEACqE;EACrE,SAAS;EACT,eAAe;EACf,qBAAqB;EACrB,yBAAyB;EACzB,oBAAoB;AACtB;;AAEA;EACE,sBAAsB;AACxB;;AAEA,iCAAiC;;AAEjC;EACE,mBAAmB;AACrB;;AAEA,4EAA4E;AAC5E;EACE,aAAa;EACb,cAAc;EACd,sBAAsB;AACxB;;AAEA;EACE,cAAc;AAChB;;AAEA;EACE,cAAc;EACd,sBAAsB;EACtB,iBAAiB;EACjB,sBAAsB;EACtB,yBAAyB;EACzB,mBAAmB;AACrB;;AAEA;EACE,kBAAkB;EAClB,iBAAiB;EACjB,mBAAmB;AACrB;;AAEA;EACE,aAAa;EACb,eAAe;EACf,WAAW;AACb;;AAEA;EACE,eAAe;EACf,YAAY;EACZ,uBAAuB;EACvB,+BAA+B;EAC/B,4BAA4B;EAC5B,4BAA4B;EAC5B,kBAAkB;EAClB,aAAa;AACf;;AAEA;EACE,WAAW;EACX,iEAAiE;EACjE,kBAAkB;EAClB,gBAAgB;EAChB,gBAAgB;AAClB;;AAEA;EACE,mBAAmB;AACrB;;AAEA;EACE,2BAA2B;EAC3B,iBAAiB;AACnB;;AAEA,kCAAkC;;AAElC;EACE,cAAc;EACd,kBAAkB;EAClB,kBAAkB;AACpB;;AAEA;iEACiE;AACjE;EACE,mBAAmB;AACrB;;AAEA;EACE,kBAAkB;AACpB;;AAEA;8DAC8D;AAC9D;EACE,oBAAoB;AACtB;;AAEA;EACE,mBAAmB;AACrB;;AAEA,kCAAkC;;AAElC;EACE,oBAAoB;EACpB,mBAAmB;EACnB,uBAAuB;EACvB,WAAW;EACX,0BAA0B;EAC1B,uBAAuB;EACvB,+BAA+B;EAC/B,4BAA4B;EAC5B,4BAA4B;EAC5B,oBAAoB;EACpB,aAAa;EACb,kBAAkB;EAClB,sBAAsB;EACtB,eAAe;AACjB;;AAEA;EACE,2BAA2B;AAC7B;;AAEA;EACE,YAAY;EACZ,mBAAmB;AACrB;;AAEA;EACE,2BAA2B;EAC3B,yBAAyB;EACzB,uBAAuB;EACvB,gBAAgB;AAClB;;AAEA;EACE,2BAA2B;EAC3B,oBAAoB;AACtB;;AAEA;EACE,yBAAyB;EACzB,uBAAuB;AACzB;;AAEA;EACE,WAAW;AACb;;AAEA;EACE,wBAAwB;EACxB,6BAA6B;EAC7B,4BAA4B;EAC5B,gBAAgB;EAChB,oBAAoB;EACpB,kBAAkB;EAClB,cAAc;EACd,eAAe;AACjB;;AAEA;EACE,oBAAoB;EACpB,2BAA2B;AAC7B;;AAEA;iDACiD;AACjD;;EAEE,gCAAgC;EAChC,mBAAmB;AACrB;;AAEA,gCAAgC;;AAEhC;EACE,aAAa;EACb,eAAe;EACf,mBAAmB;EACnB,8BAA8B;EAC9B,eAAe;EACf;iEAC+D;EAC/D,0BAA0B;AAC5B;;AAEA;EACE,6EAA6E;EAC7E;IACE,aAAa;EACf;AACF;;AAEA,yDAAyD;AACzD;EACE,aAAa;EACb,mBAAmB;EACnB,uBAAuB;EACvB,SAAS;EACT,aAAa;EACb,8BAA8B;EAC9B,4BAA4B;EAC5B,mBAAmB;EACnB,kBAAkB;AACpB;;AAEA;EACE,kBAAkB;EAClB,aAAa;EACb,6BAA6B;EAC7B,4BAA4B;EAC5B,0BAA0B;AAC5B;;AAEA;EACE,2BAA2B;AAC7B;;AAEA;EACE,0BAA0B;EAC1B,kBAAkB;AACpB;;AAEA;4DAC4D;AAC5D;EACE,UAAU;EACV,SAAS;EACT,gBAAgB;EAChB,oBAAoB;EACpB,aAAa;EACb,gBAAgB;EAChB,eAAe;AACjB;;AAEA;EACE,WAAW;EACX,kBAAkB;EAClB,QAAQ;EACR,4BAA4B;AAC9B;;AAEA;EACE,SAAS;EACT,qBAAqB;EACrB,iBAAiB;AACnB;;AAEA;EACE,mBAAmB;EACnB,mBAAmB;EACnB,kBAAkB;AACpB;;AAEA;EACE,kBAAkB;EAClB,mBAAmB;EACnB,iBAAiB;AACnB;;AAEA;EACE,kBAAkB;EAClB,WAAW;EACX,aAAa;EACb,oDAAoD;EACpD,UAAU;EACV,sBAAsB;EACtB,6BAA6B;EAC7B,4BAA4B;EAC5B,gBAAgB;EAChB,oBAAoB;EACpB,iBAAiB;EACjB,cAAc;EACd,eAAe;AACjB;;AAEA;EACE,oBAAoB;EACpB,2BAA2B;AAC7B;;AAEA,iCAAiC;;AAEjC;EACE,uBAAuB;EACvB,oBAAoB;EACpB,2BAA2B;EAC3B,wBAAwB;EACxB,iBAAiB;EACjB,sBAAsB;EACtB,yBAAyB;EACzB,mBAAmB;AACrB;;AAEA,kCAAkC;;AAElC;;;mDAGmD;AACnD;EACE,qCAAqC;EACrC,8BAA8B;EAC9B,+BAA+B;EAC/B,UAAU;EACV,+BAA+B;EAC/B,4BAA4B;EAC5B,0BAA0B;EAC1B,kBAAkB;EAClB,gBAAgB;AAClB;;AAEA;EACE,4BAA4B;AAC9B;;AAEA;EACE,aAAa;EACb,sBAAsB;EACtB,8BAA8B;EAC9B,+BAA+B;AACjC;;AAEA;EACE,aAAa;EACb,uBAAuB;EACvB,8BAA8B;EAC9B,eAAe;EACf,aAAa;EACb,oCAAoC;AACtC;;AAEA;EACE,SAAS;EACT,iBAAiB;EACjB,oBAAoB;AACtB;;AAEA;EACE,kBAAkB;EAClB,mBAAmB;EACnB,kBAAkB;AACpB;;AAEA;EACE,aAAa;EACb,gBAAgB;AAClB;;AAEA;EACE,aAAa;EACb,eAAe;EACf,yBAAyB;EACzB,WAAW;EACX,aAAa;EACb,iCAAiC;AACnC;;AAEA,kCAAkC;;AAElC,6EAA6E;AAC7E;EACE,gBAAgB;AAClB;;AAEA;EACE,SAAS;AACX;;AAEA;EACE,iBAAiB;EACjB,qBAAqB;EACrB,yBAAyB;EACzB,oBAAoB;AACtB;;AAEA;EACE,sBAAsB;AACxB;;AAEA;EACE,gBAAgB;AAClB;;AAEA,oCAAoC;;AAEpC;;;gEAGgE;AAChE;EACE,cAAc;EACd,gBAAgB;AAClB;;AAEA;EACE,4CAA4C;EAC5C,mBAAmB;EACnB,mBAAmB;AACrB;;AAEA;EACE,gBAAgB;AAClB;;AAEA;EACE,mBAAmB;EACnB,kBAAkB;EAClB,qBAAqB;EACrB,yBAAyB;EACzB,oBAAoB;AACtB;;AAEA;EACE,SAAS;EACT,UAAU;EACV,gBAAgB;AAClB;;AAEA;EACE,aAAa;EACb,qBAAqB;EACrB,WAAW;EACX,kBAAkB;EAClB,oCAAoC;EACpC,kBAAkB;AACpB;;AAEA;EACE,cAAc;EACd,YAAY;EACZ,uBAAuB;AACzB;;AAEA;EACE,kBAAkB;EAClB,kCAAkC;AACpC;;AAEA;EACE,mBAAmB;EACnB,kBAAkB;AACpB;;AAEA,yCAAyC;;AAEzC;;0CAE0C;AAC1C;EACE,iBAAiB;EACjB,qBAAqB;EACrB,kBAAkB;EAClB,gBAAgB;EAChB,sBAAsB;EACtB,yBAAyB;AAC3B;;AAEA,2DAA2D;AAC3D;EACE,cAAc;EACd,mBAAmB;EACnB,iBAAiB;AACnB;;AAEA,mCAAmC;;AAEnC;EACE,SAAS;EACT,UAAU;EACV,gBAAgB;AAClB;;AAEA;EACE,aAAa;EACb,mBAAmB;EACnB,WAAW;EACX,kBAAkB;EAClB,oCAAoC;AACtC;;AAEA,6CAA6C;AAC7C;EACE,cAAc;EACd,YAAY;EACZ,uBAAuB;EACvB,oBAAoB;AACtB;;AAEA,oCAAoC;;AAEpC;EACE,kBAAkB;EAClB,UAAU;EACV,WAAW;EACX,UAAU;EACV,YAAY;EACZ,gBAAgB;EAChB,qBAAqB;EACrB,mBAAmB;EACnB,SAAS;AACX;;AAEA,2CAA2C;;AAE3C;EACE;;;IAGE,qCAAqC;IACrC,uCAAuC;IACvC,sCAAsC;EACxC;AACF;;AAEA;EACE;IACE,qBAAqB;IACrB,oBAAoB;EACtB;AACF;;AAEA;iEACiE;AACjE;EACE;IACE,aAAa;EACf;;EAEA;;;;;IAKE,4BAA4B;EAC9B;;EAEA;qEACmE;EACnE;IACE,UAAU;EACZ;;EAEA;;IAEE,4BAA4B;EAC9B;AACF","sourcesContent":["/*\n * Aether Fleet Ops.\n *\n * Every colour pair here has been measured against WCAG 2.1 AA in both\n * editions: 4.5:1 for text, 3:1 for control borders and the focus ring. The\n * dark edition is the default and the light one is served to anyone whose\n * system asks for it.\n */\n\n:root {\n  color-scheme: dark;\n\n  /* Neutral greys, so the two hues below are the only colour on the page. */\n  --bg: #1c1d21;\n  --surface: #25262c;\n  --surface-2: #2e3037;\n\n  /* Blue is for reading: every piece of text that is not one of the things\n     below. Orange is for structure and action: headings, ship names and button\n     labels, plus the focus ring. Red is kept for errors and destructive\n     buttons, so that a failure never reads as ordinary text. */\n  --text: #4fb4d8;\n  --muted: #6fa0b4;\n  --accent: #ef7c2a;\n  --danger: #f26d7f;\n  /* Set apart from the blue around it without being an action: a ship's hull,\n     and the verb that leads a step of a fit comparison. */\n  --neutral: #cbcdd2;\n\n  --on-accent: #1c1d21;\n  --on-danger: #1c1d21;\n\n  --badge-bg: #1f3743;\n  --badge-text: #4fb4d8;\n\n  /* Meets 1.4.11 against --bg, --surface and --surface-2. */\n  --border: #848794;\n  /* Purely decorative separators, which 1.4.11 does not govern. */\n  --rule: #45474f;\n\n  --radius: 8px;\n  --gap: 1rem;\n  /* The height of a button, shared with rows that must line up with one. */\n  --control: 2.5rem;\n  --measure: 68rem;\n}\n\n@media (prefers-color-scheme: light) {\n  :root {\n    color-scheme: light;\n\n    --bg: #f1f2f4;\n    --surface: #ffffff;\n    --surface-2: #f6f7f9;\n\n    /* The same blue, orange and red, darkened until they carry text on a light\n       ground. */\n    --text: #1a6e8e;\n    --muted: #34728c;\n    --accent: #a84d0b;\n    --danger: #c2203a;\n    --neutral: #1c1d21;\n\n    --on-accent: #ffffff;\n    --on-danger: #ffffff;\n\n    --badge-bg: #d9ecf3;\n    --badge-text: #1a6380;\n\n    --border: #848794;\n    --rule: #d5d7dc;\n  }\n}\n\n*,\n*::before,\n*::after {\n  box-sizing: border-box;\n}\n\nbody {\n  margin: 0;\n  background: var(--bg);\n  color: var(--text);\n  font:\n    16px/1.5 'Segoe UI',\n    system-ui,\n    -apple-system,\n    sans-serif;\n  /* 1.4.10: the page must reflow, never scroll sideways. */\n  overflow-x: hidden;\n}\n\n/* A drifting field of stars, decorative only. */\n.starfield {\n  position: fixed;\n  inset: 0;\n  z-index: -1;\n  pointer-events: none;\n  background:\n    radial-gradient(1px 1px at 20% 30%, var(--muted), transparent),\n    radial-gradient(1px 1px at 70% 60%, var(--muted), transparent),\n    radial-gradient(1px 1px at 40% 80%, var(--muted), transparent),\n    radial-gradient(1px 1px at 85% 15%, var(--muted), transparent);\n  opacity: 0.35;\n  animation: drift 90s linear infinite;\n}\n\n@keyframes drift {\n  to {\n    transform: translateY(-40px);\n  }\n}\n\n.app {\n  max-width: var(--measure);\n  margin: 0 auto;\n  /* Vertical only, so the side gutter below is never overridden. */\n  padding-block: 1.5rem;\n  padding-inline: 1rem;\n}\n\n/* ---------- banner ---------- */\n\n.banner {\n  display: flex;\n  flex-wrap: wrap;\n  align-items: center;\n  justify-content: space-between;\n  gap: var(--gap);\n  padding-bottom: 1rem;\n  border-bottom: 1px solid var(--rule);\n}\n\n.banner__logo {\n  display: flex;\n  align-items: center;\n  gap: 0.5rem;\n  margin: 0;\n  font-size: 1.35rem;\n  letter-spacing: 0.06em;\n  text-transform: uppercase;\n  color: var(--accent);\n}\n\n.banner__glyph {\n  color: var(--text);\n}\n\n.stats {\n  display: flex;\n  gap: 1.5rem;\n  margin: 0;\n}\n\n.stat__label {\n  font-size: 0.7rem;\n  letter-spacing: 0.12em;\n  text-transform: uppercase;\n  color: var(--muted);\n}\n\n.stat__value {\n  margin: 0;\n  font-size: 1.5rem;\n  font-variant-numeric: tabular-nums;\n  color: var(--text);\n}\n\n/* ---------- toolbar ---------- */\n\n/* No padding of its own: the space around the toolbar belongs to the grid it\n   sits in, so the fleet heading beside it starts at exactly the same height. */\n.toolbar {\n  display: flex;\n  flex-wrap: wrap;\n  gap: 0.5rem;\n}\n\n/* The buttons share out the row between them, so the toolbar's edges line up\n   with the panel beneath it. The basis stays `auto` so that a row too narrow\n   for all three still wraps, and each wrapped row is filled in turn. */\n.toolbar > .btn {\n  flex: 1 1 auto;\n}\n\n/* ---------- layout ---------- */\n\n/*\n * The page is one grid of equal columns, and every ship is one cell of it.\n *\n *   row 1      toolbar (2 columns)   | fleet heading (the rest)\n *   rows 2-5   intake  (2 columns)   | ships\n *   rows 6+    ships, wrapping round beneath the intake\n *\n * The intake is two columns wide and four rows tall. Every row after the first\n * is `1fr`, and with no height set on the grid that makes them all as tall as\n * the most demanding one. The intake, shared across its four rows, is the most\n * demanding, so four ships and the three gaps between them are exactly as tall\n * as the intake, however tall its contents make it. Nothing here is a measured\n * number that could drift.\n */\n.main {\n  display: grid;\n  gap: var(--gap);\n  /* Two columns and a gap hold the three toolbar buttons on one row. */\n  grid-template-columns: repeat(auto-fill, minmax(min(100%, 12rem), 1fr));\n  grid-template-rows: auto;\n  grid-auto-rows: 1fr;\n  padding-top: 1rem;\n}\n\n.toolbar {\n  grid-column: 1 / span 2;\n  grid-row: 1;\n}\n\n.intake {\n  grid-column: 1 / span 2;\n  grid-row: 2 / span 4;\n}\n\n/* The section stays in the document as the fleet's landmark but makes no box,\n   which is what lets its heading and its ships be cells of the grid above. */\n.fleet {\n  display: contents;\n}\n\n.fleet__head {\n  grid-column: 3 / -1;\n  grid-row: 1;\n}\n\n.fleet__empty {\n  grid-column: 3 / -1;\n}\n\n/* Too narrow to be sure of a third column, so nothing sits beside the intake.\n   The toolbar, the intake and the fleet heading each take the full width, and\n   the ships follow in as many columns as fit. The intake still spans four\n   rows, so a ship is the same height here as in the wider layout. The\n   breakpoint is a little above the width where a third column first fits,\n   because a scrollbar takes its width out of the grid but not out of the\n   media query. */\n@media (max-width: 44rem) {\n  .main {\n    grid-template-rows: auto repeat(4, 1fr) auto;\n  }\n\n  .toolbar,\n  .intake,\n  .fleet__head,\n  .fleet__empty {\n    grid-column: 1 / -1;\n  }\n\n  .fleet__head {\n    grid-row: 6;\n  }\n}\n\n/* ---------- intake ---------- */\n\n/* A column, so that when the grid makes the panel taller than its contents the\n   fitting field takes up the difference and the panel is never left with dead\n   space at the bottom. That happens when a ship's card needs more height than a\n   quarter of the panel, which raises every row. */\n.intake {\n  display: flex;\n  flex-direction: column;\n  padding: 1.25rem;\n  border: 1px solid var(--rule);\n  border-radius: var(--radius);\n  background: var(--surface);\n}\n\n.intake h2,\n.fleet__head h2 {\n  /* No bottom margin: the spacing below each heading belongs to its container,\n     which is the only thing that knows how tall the row actually is. */\n  margin: 0;\n  font-size: 1rem;\n  letter-spacing: 0.1em;\n  text-transform: uppercase;\n  color: var(--accent);\n}\n\n.intake h2 {\n  margin-bottom: 0.75rem;\n}\n\n/* ---------- fields ---------- */\n\n.field {\n  margin-bottom: 1rem;\n}\n\n/* Takes any spare height in a column container and hands it to its input. */\n.field--grow {\n  display: flex;\n  flex: 1 0 auto;\n  flex-direction: column;\n}\n\n.field--grow > .field__input {\n  flex: 1 0 auto;\n}\n\n.field__label {\n  display: block;\n  margin-bottom: 0.25rem;\n  font-size: 0.8rem;\n  letter-spacing: 0.08em;\n  text-transform: uppercase;\n  color: var(--muted);\n}\n\n.field__hint {\n  margin: 0 0 0.5rem;\n  font-size: 0.8rem;\n  color: var(--muted);\n}\n\n.field__row {\n  display: flex;\n  flex-wrap: wrap;\n  gap: 0.5rem;\n}\n\n.field__input {\n  flex: 1 1 10rem;\n  min-width: 0;\n  padding: 0.55rem 0.7rem;\n  border: 1px solid var(--border);\n  border-radius: var(--radius);\n  background: var(--surface-2);\n  color: var(--text);\n  font: inherit;\n}\n\n.field__input--fit {\n  width: 100%;\n  font-family: ui-monospace, 'Cascadia Mono', 'Consolas', monospace;\n  font-size: 0.85rem;\n  resize: vertical;\n  white-space: pre;\n}\n\n.field__input::placeholder {\n  color: var(--muted);\n}\n\n.field__input[aria-invalid='true'] {\n  border-color: var(--danger);\n  border-width: 2px;\n}\n\n/* ---------- notices ---------- */\n\n.notice {\n  display: block;\n  margin: 0.4rem 0 0;\n  font-size: 0.85rem;\n}\n\n/* The parse result stands on its own between two fields and needs the\n   breathing room a field's own margin would otherwise give it. */\noutput.notice {\n  margin-bottom: 1rem;\n}\n\n.notice--good {\n  color: var(--text);\n}\n\n/* Colour is never the only carrier: a failure also states its reason in\n   words, and the field it refers to is marked aria-invalid. */\n.notice--bad {\n  color: var(--danger);\n}\n\n.notice--muted {\n  color: var(--muted);\n}\n\n/* ---------- buttons ---------- */\n\n.btn {\n  display: inline-flex;\n  align-items: center;\n  justify-content: center;\n  gap: 0.4rem;\n  min-height: var(--control);\n  padding: 0.55rem 0.9rem;\n  border: 1px solid var(--border);\n  border-radius: var(--radius);\n  background: var(--surface-2);\n  color: var(--accent);\n  font: inherit;\n  font-size: 0.85rem;\n  letter-spacing: 0.04em;\n  cursor: pointer;\n}\n\n.btn:hover:not(:disabled) {\n  border-color: var(--accent);\n}\n\n.btn:disabled {\n  opacity: 0.5;\n  cursor: not-allowed;\n}\n\n.btn--primary {\n  border-color: var(--accent);\n  background: var(--accent);\n  color: var(--on-accent);\n  font-weight: 600;\n}\n\n.btn--danger {\n  border-color: var(--danger);\n  color: var(--danger);\n}\n\n.btn--danger:hover:not(:disabled) {\n  background: var(--danger);\n  color: var(--on-danger);\n}\n\n.btn--wide {\n  width: 100%;\n}\n\n.icon-btn {\n  padding: 0.15rem 0.45rem;\n  border: 1px solid transparent;\n  border-radius: var(--radius);\n  background: none;\n  color: var(--accent);\n  font-size: 1.25rem;\n  line-height: 1;\n  cursor: pointer;\n}\n\n.icon-btn:hover {\n  color: var(--danger);\n  border-color: var(--danger);\n}\n\n/* A single visible focus indicator for every control, meeting 1.4.11\n   against both the page and panel backgrounds. */\n:where(a, button, input, textarea, [tabindex]):focus-visible,\nlabel.btn:has(input:focus-visible) {\n  outline: 3px solid var(--accent);\n  outline-offset: 2px;\n}\n\n/* ---------- fleet ---------- */\n\n.fleet__head {\n  display: flex;\n  flex-wrap: wrap;\n  align-items: center;\n  justify-content: space-between;\n  gap: var(--gap);\n  /* As tall as the toolbar beside it whether or not \"Clear All\" is showing, so\n     the heading sits on the same line as the toolbar's labels. */\n  min-height: var(--control);\n}\n\n@media (max-width: 44rem) {\n  /* Stacked, nothing sits beside the heading, so it need not match a button. */\n  .fleet__head {\n    min-height: 0;\n  }\n}\n\n/* Fills a ship's cell, with the message centred in it. */\n.fleet__empty {\n  display: flex;\n  align-items: center;\n  justify-content: center;\n  margin: 0;\n  padding: 1rem;\n  border: 1px dashed var(--rule);\n  border-radius: var(--radius);\n  color: var(--muted);\n  text-align: center;\n}\n\n.card {\n  position: relative;\n  padding: 1rem;\n  border: 1px solid var(--rule);\n  border-radius: var(--radius);\n  background: var(--surface);\n}\n\n.card:hover {\n  border-color: var(--border);\n}\n\n.card__name {\n  margin: 0 1.5rem 0.25rem 0;\n  font-size: 1.05rem;\n}\n\n/* The name is the card's only tab stop; stretching it over the card gives a\n   pointer the whole surface without adding a second stop. */\n.card__open {\n  padding: 0;\n  border: 0;\n  background: none;\n  color: var(--accent);\n  font: inherit;\n  text-align: left;\n  cursor: pointer;\n}\n\n.card__open::after {\n  content: '';\n  position: absolute;\n  inset: 0;\n  border-radius: var(--radius);\n}\n\n.card__hull {\n  margin: 0;\n  color: var(--neutral);\n  font-size: 0.9rem;\n}\n\n.card__title {\n  margin: 0.15rem 0 0;\n  color: var(--muted);\n  font-size: 0.85rem;\n}\n\n.card__meta {\n  margin: 0.5rem 0 0;\n  color: var(--muted);\n  font-size: 0.8rem;\n}\n\n.card__scuttle {\n  position: absolute;\n  top: 0.5rem;\n  right: 0.5rem;\n  /* Above the stretched name so it stays clickable. */\n  z-index: 1;\n  padding: 0.1rem 0.4rem;\n  border: 1px solid transparent;\n  border-radius: var(--radius);\n  background: none;\n  color: var(--accent);\n  font-size: 1.1rem;\n  line-height: 1;\n  cursor: pointer;\n}\n\n.card__scuttle:hover {\n  color: var(--danger);\n  border-color: var(--danger);\n}\n\n/* ---------- badges ---------- */\n\n.badge {\n  padding: 0.1rem 0.45rem;\n  border-radius: 999px;\n  background: var(--badge-bg);\n  color: var(--badge-text);\n  font-size: 0.7rem;\n  letter-spacing: 0.06em;\n  text-transform: uppercase;\n  white-space: nowrap;\n}\n\n/* ---------- dialogs ---------- */\n\n/* Heights are in `dvh`, the viewport as it is right now. On a phone `vh` is the\n   viewport with the browser's bars hidden, so a dialog sized to it runs off the\n   top and bottom of the screen whenever the bars are showing. The `vh` line\n   before each one is for browsers without `dvh`. */\n.modal {\n  width: min(46rem, calc(100vw - 2rem));\n  max-height: calc(100vh - 2rem);\n  max-height: calc(100dvh - 2rem);\n  padding: 0;\n  border: 1px solid var(--border);\n  border-radius: var(--radius);\n  background: var(--surface);\n  color: var(--text);\n  overflow: hidden;\n}\n\n.modal::backdrop {\n  background: rgb(0 0 0 / 65%);\n}\n\n.modal__panel {\n  display: flex;\n  flex-direction: column;\n  max-height: calc(100vh - 2rem);\n  max-height: calc(100dvh - 2rem);\n}\n\n.modal__head {\n  display: flex;\n  align-items: flex-start;\n  justify-content: space-between;\n  gap: var(--gap);\n  padding: 1rem;\n  border-bottom: 1px solid var(--rule);\n}\n\n.modal__title {\n  margin: 0;\n  font-size: 1.1rem;\n  color: var(--accent);\n}\n\n.modal__subtitle {\n  margin: 0.2rem 0 0;\n  color: var(--muted);\n  font-size: 0.85rem;\n}\n\n.modal__body {\n  padding: 1rem;\n  overflow-y: auto;\n}\n\n.modal__foot {\n  display: flex;\n  flex-wrap: wrap;\n  justify-content: flex-end;\n  gap: 0.5rem;\n  padding: 1rem;\n  border-top: 1px solid var(--rule);\n}\n\n/* ---------- choices ---------- */\n\n/* A question with named answers: each answer's name, then what it will do. */\n.choice__lead {\n  margin: 0 0 1rem;\n}\n\n.choice {\n  margin: 0;\n}\n\n.choice__name {\n  font-size: 0.8rem;\n  letter-spacing: 0.1em;\n  text-transform: uppercase;\n  color: var(--accent);\n}\n\n.choice__outcome {\n  margin: 0.15rem 0 1rem;\n}\n\n.choice__outcome:last-child {\n  margin-bottom: 0;\n}\n\n/* ---------- fit sheet ---------- */\n\n/* Multi-column rather than grid: a fit's sections vary wildly in length — one\n   low slot beside eight high slots is normal — and grid rows are all as tall as\n   their tallest cell, which strands a short section above a large gap. Columns\n   let each section take only the height it needs and flow on. */\n.sheet {\n  columns: 16rem;\n  column-gap: 1rem;\n}\n\n.sheet__section {\n  /* Keep a section's heading with its list. */\n  break-inside: avoid;\n  margin-bottom: 1rem;\n}\n\n.sheet__section:last-child {\n  margin-bottom: 0;\n}\n\n.sheet__heading {\n  margin: 0 0 0.35rem;\n  font-size: 0.75rem;\n  letter-spacing: 0.1em;\n  text-transform: uppercase;\n  color: var(--accent);\n}\n\n.sheet__list {\n  margin: 0;\n  padding: 0;\n  list-style: none;\n}\n\n.sheet__item {\n  display: flex;\n  align-items: baseline;\n  gap: 0.4rem;\n  padding: 0.25rem 0;\n  border-bottom: 1px solid var(--rule);\n  font-size: 0.85rem;\n}\n\n.sheet__name {\n  flex: 1 1 auto;\n  min-width: 0;\n  overflow-wrap: anywhere;\n}\n\n.sheet__qty {\n  color: var(--text);\n  font-variant-numeric: tabular-nums;\n}\n\n.sheet__item--empty {\n  color: var(--muted);\n  font-style: italic;\n}\n\n/* ---------- fit comparison ---------- */\n\n/* What to do, ahead of what to do it to. The word carries the meaning, so\n   nothing here depends on colour. A fixed width lines the items up beneath one\n   another whichever verb leads the row. */\n.diff__verb {\n  flex: 0 0 4.25rem;\n  color: var(--neutral);\n  font-size: 0.75rem;\n  font-weight: 600;\n  letter-spacing: 0.08em;\n  text-transform: uppercase;\n}\n\n/* Where a charge goes, on its own line under the charge. */\n.diff__detail {\n  display: block;\n  color: var(--muted);\n  font-size: 0.8rem;\n}\n\n/* ---------- registry ---------- */\n\n.registry {\n  margin: 0;\n  padding: 0;\n  list-style: none;\n}\n\n.registry__row {\n  display: flex;\n  align-items: center;\n  gap: 0.5rem;\n  padding: 0.35rem 0;\n  border-bottom: 1px solid var(--rule);\n}\n\n/* A ship name, like the ones on the cards. */\n.registry__name {\n  flex: 1 1 auto;\n  min-width: 0;\n  overflow-wrap: anywhere;\n  color: var(--accent);\n}\n\n/* ---------- utilities ---------- */\n\n.sr-only {\n  position: absolute;\n  width: 1px;\n  height: 1px;\n  padding: 0;\n  margin: -1px;\n  overflow: hidden;\n  clip-path: inset(50%);\n  white-space: nowrap;\n  border: 0;\n}\n\n/* ---------- user preferences ---------- */\n\n@media (prefers-reduced-motion: reduce) {\n  *,\n  *::before,\n  *::after {\n    animation-duration: 0.01ms !important;\n    animation-iteration-count: 1 !important;\n    transition-duration: 0.01ms !important;\n  }\n}\n\n@media (prefers-contrast: more) {\n  :root {\n    --rule: var(--border);\n    --muted: var(--text);\n  }\n}\n\n/* In forced-colours mode the system palette wins. Keep the structure that\n   carries meaning and drop the decoration that would fight it. */\n@media (forced-colors: active) {\n  .starfield {\n    display: none;\n  }\n\n  .btn,\n  .card,\n  .intake,\n  .modal,\n  .field__input {\n    border: 1px solid CanvasText;\n  }\n\n  /* The system already paints a disabled control in its own GrayText. Fading\n     it as well takes a colour the user chose and makes it fainter. */\n  .btn:disabled {\n    opacity: 1;\n  }\n\n  :where(a, button, input, textarea):focus-visible,\n  label.btn:has(input:focus-visible) {\n    outline: 3px solid Highlight;\n  }\n}\n"],"sourceRoot":""}]);
// Exports
export default ___CSS_LOADER_EXPORT___;
